import { FC } from "react";

import { Box, Stack, useTheme } from "@mui/material";
import { getTextColorOfHexColor, Locales } from "@simplyk/common";
import countryRegionData, { Country } from "country-region-data";
import { UseFormReturn, useWatch } from "react-hook-form";

import { NewsletterFormSignupData } from "../Editor/types";

import { NewsletterFormSignUpLayout } from "./NewsletterFormSignUpLayout";

import { useCurrentUserContext } from "@/contexts/CurrentUserContext";
import { DashboardAutocomplete } from "@/dashboard-design-system/Autocomplete/DashboardAutocomplete";
import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardEmailTextField } from "@/dashboard-design-system/EmailTextField";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { DashboardPhoneNumberInput } from "@/dashboard-design-system/PhoneNumberInput";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { isEmail, isNameValid, isRequired } from "@/helpers/validators";
import { useCountryRegion } from "@/hooks/useCountryRegion";
import { useTranslate } from "@/hooks/useTranslate";

const getCountryLabel = (country: Country) => `${country.countryName} (${country.countryShortCode})`;

export const NewsletterFormSignUpStateless: FC<{
  mainColor: string;
  buttonColor: string;
  title: string;
  submitState: "submitted" | "submitting" | "idle";
  form: UseFormReturn<NewsletterFormSignupData>;
  isEmbedded?: boolean;
  isPreview?: boolean;
  hasFirstName?: boolean;
  hasLastName?: boolean;
  hasPhoneNumber?: boolean;
  hasAddress?: boolean;
  hasLanguage?: boolean;
  hasCommunicationPreferences?: boolean;
  communicationPreferences?: Array<{
    id: string;
    label: string;
    contactListId: string;
  }>;
}> = ({
  mainColor,
  buttonColor,
  title,
  submitState,
  form,
  isEmbedded,
  isPreview,
  hasFirstName,
  hasLastName,
  hasPhoneNumber,
  hasAddress,
  hasLanguage,
  hasCommunicationPreferences,
  communicationPreferences = [],
}) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { organization } = useCurrentUserContext();

  const { errors } = form.formState;
  const country = useWatch({ control: form.control, name: "country" });
  const { currentCountryRegions } = useCountryRegion(country);

  const mainTextColor = getTextColorOfHexColor(mainColor);
  const buttonTextColor = getTextColorOfHexColor(buttonColor);

  return (
    <NewsletterFormSignUpLayout
      isEmbedded={isEmbedded}
      backgroundColor={mainColor}
      slots={{
        title: (
          <DashboardTypography variant="h4" sx={{ color: mainTextColor }}>
            {title}
          </DashboardTypography>
        ),
        // Update the fields section to match the Figma layout
        fields: (
          <Stack gap={1}>
            {/* Name fields - only show if enabled */}
            {(hasFirstName || hasLastName) && (
              <Stack direction="row" gap={1}>
                {hasFirstName && (
                  <DashboardTextField
                    name="firstName"
                    control={form.control}
                    placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.firstName")}
                    rules={[
                      isRequired(t("common", "firstName.required")),
                      isNameValid(t("common", "invalidCharacterUsed")),
                    ]}
                    errorMessage={errors.firstName?.message}
                    disableAutocomplete
                    disabled={isPreview}
                    style={
                      isPreview
                        ? {
                            opacity: 1,
                            pointerEvents: "none",
                            backgroundColor: theme.palette.surface.neutral.white,
                            borderColor: theme.palette.border.neutral.quiet,
                          }
                        : {}
                    }
                  />
                )}
                {hasLastName && (
                  <DashboardTextField
                    name="lastName"
                    control={form.control}
                    placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.lastName")}
                    rules={[
                      isRequired(t("common", "lastName.required")),
                      isNameValid(t("common", "invalidCharacterUsed")),
                    ]}
                    errorMessage={errors.lastName?.message}
                    disableAutocomplete
                    disabled={isPreview}
                    style={
                      isPreview
                        ? {
                            opacity: 1,
                            pointerEvents: "none",
                            backgroundColor: theme.palette.surface.neutral.white,
                            borderColor: theme.palette.border.neutral.quiet,
                          }
                        : {}
                    }
                  />
                )}
              </Stack>
            )}

            {/* Email - always shown */}
            <DashboardEmailTextField
              name="email"
              control={form.control}
              placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.email")}
              rules={[
                isRequired(t("common", "emailAddress.required")),
                isEmail(t("common", "emailFormat"), t("common", "emailFormat.wrongChar")),
              ]}
              errorMessage={errors.email?.message}
              disableAutocomplete
              disabled={isPreview}
              style={
                isPreview
                  ? {
                      opacity: 1,
                      pointerEvents: "none",
                      backgroundColor: theme.palette.surface.neutral.white,
                      borderColor: theme.palette.border.neutral.quiet,
                    }
                  : {}
              }
            />

            {/* Phone Number - only show if enabled */}
            {hasPhoneNumber && (
              <DashboardPhoneNumberInput
                name="phoneNumber"
                control={form.control}
                rules={[isRequired(t("dashboard", "newsletterForms.editor.step.questions.field.phoneNumber.required"))]}
                errorMessage={errors.phoneNumber?.message}
                noAsterisk
                organizationCountry={organization?.country}
                disabled={isPreview}
              />
            )}

            {/* Address Section - only show if enabled */}
            {hasAddress && (
              <Stack gap={1}>
                {/* Address Line */}
                <DashboardTextField
                  name="address"
                  control={form.control}
                  placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.address")}
                  rules={[isRequired(t("dashboard", "newsletterForms.editor.step.questions.field.address.required"))]}
                  errorMessage={errors.address?.message}
                  disableAutocomplete
                  disabled={isPreview}
                  style={
                    isPreview
                      ? {
                          opacity: 1,
                          pointerEvents: "none",
                          backgroundColor: theme.palette.surface.neutral.white,
                          borderColor: theme.palette.border.neutral.quiet,
                        }
                      : {}
                  }
                />

                {/* ZIP Code and City */}
                <Stack direction="row" gap={1}>
                  <DashboardTextField
                    name="zipCode"
                    control={form.control}
                    placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.postalCode")}
                    rules={[
                      isRequired(t("dashboard", "newsletterForms.editor.step.questions.field.postalCode.required")),
                    ]}
                    errorMessage={errors.zipCode?.message}
                    disableAutocomplete
                    disabled={isPreview}
                    style={
                      isPreview
                        ? {
                            opacity: 1,
                            pointerEvents: "none",
                            backgroundColor: theme.palette.surface.neutral.white,
                            borderColor: theme.palette.border.neutral.quiet,
                          }
                        : {}
                    }
                  />
                  <DashboardTextField
                    name="city"
                    control={form.control}
                    placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.city")}
                    rules={[isRequired(t("dashboard", "newsletterForms.editor.step.questions.field.city.required"))]}
                    errorMessage={errors.city?.message}
                    disableAutocomplete
                    disabled={isPreview}
                    style={
                      isPreview
                        ? {
                            opacity: 1,
                            pointerEvents: "none",
                            backgroundColor: theme.palette.surface.neutral.white,
                            borderColor: theme.palette.border.neutral.quiet,
                          }
                        : {}
                    }
                  />
                </Stack>

                {/* State and country dropdown fields  */}
                <Stack direction="row" gap={1}>
                  <DashboardSelect
                    control={form.control}
                    name="country"
                    rules={[isRequired(t("common", "fieldIsRequired"))]}
                    data-test="form-country"
                    placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.country")}
                    renderValue={(value) => {
                      if (!value) {
                        return t("dashboard", "newsletterForms.editor.step.questions.field.country");
                      }
                      const country = countryRegionData.find((country) => country.countryName === value);
                      if (country) {
                        return getCountryLabel(country);
                      }
                      return value.toString();
                    }}
                    required
                    error={Boolean(form.formState.errors.country?.message)}
                    helperText={form.formState.errors.country?.message}
                    sx={
                      isPreview
                        ? {
                            opacity: 1,
                            pointerEvents: "none",
                            backgroundColor: theme.palette.surface.neutral.white,
                            borderColor: theme.palette.border.neutral.quiet,
                            borderRadius: theme.radius(1),
                          }
                        : {}
                    }
                  >
                    {countryRegionData.map((country) => (
                      <DashboardMenuItem
                        key={country.countryName}
                        value={country.countryName}
                        data-test="form-country-option"
                      >
                        {getCountryLabel(country)}
                      </DashboardMenuItem>
                    ))}
                  </DashboardSelect>
                  <DashboardSelect
                    control={form.control}
                    name="state"
                    rules={[isRequired(t("common", "fieldIsRequired"))]}
                    data-test="form-region"
                    placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.region")}
                    required
                    error={Boolean(form.formState.errors.state?.message)}
                    helperText={form.formState.errors.state?.message}
                    sx={
                      isPreview
                        ? {
                            opacity: 1,
                            pointerEvents: "none",
                            backgroundColor: theme.palette.surface.neutral.white,
                            borderColor: theme.palette.border.neutral.quiet,
                            borderRadius: theme.radius(1),
                          }
                        : {}
                    }
                  >
                    {currentCountryRegions.map((region, index) => (
                      <DashboardMenuItem key={index} value={region.name} data-test="form-region-option">
                        {`${region.name} (${region.shortCode})`}
                      </DashboardMenuItem>
                    ))}
                  </DashboardSelect>
                </Stack>
              </Stack>
            )}

            {/* Language - only show if enabled */}
            {hasLanguage && (
              <>
                <DashboardSelect
                  control={form.control}
                  name="language"
                  placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.language.placeholder")}
                  rules={[isRequired(t("dashboard", "newsletterForms.editor.step.questions.field.language.required"))]}
                  data-test="form-language"
                  required
                  error={Boolean(form.formState.errors.language?.message)}
                  helperText={form.formState.errors.language?.message}
                  sx={
                    isPreview
                      ? {
                          opacity: 1,
                          pointerEvents: "none",
                          backgroundColor: theme.palette.surface.neutral.white,
                          borderColor: theme.palette.border.neutral.quiet,
                          borderRadius: theme.radius(1),
                        }
                      : {}
                  }
                >
                  <DashboardMenuItem value={Locales.EN} data-test="form-language-option">
                    {t("dashboard", `ContactFiltering.languages.${Locales.EN}`)}
                  </DashboardMenuItem>
                  <DashboardMenuItem value={Locales.FR} data-test="form-language-option">
                    {t("dashboard", `ContactFiltering.languages.${Locales.FR}`)}
                  </DashboardMenuItem>
                </DashboardSelect>
              </>
            )}

            {/* Communication Preferences - only show if enabled */}
            {hasCommunicationPreferences && communicationPreferences.length > 0 && (
              <DashboardAutocomplete
                control={form.control}
                name="communicationPreferences"
                rules={[isRequired(t("common", "fieldIsRequired"))]}
                options={communicationPreferences
                  .filter((option) => option.label.trim() !== "")
                  .map((option) => ({
                    label: option.label,
                    value: option.id,
                  }))}
                placeholder={t("dashboard", "newsletterForms.editor.step.questions.field.communicationPreferences")}
                multiple
                checkbox
                open={isPreview && communicationPreferences.some((option) => option.label.trim() !== "")}
                data-test="form-communication-preferences"
                {...(isPreview && {
                  MenuProps: {
                    disablePortal: true,
                    disableScrollLock: true,
                    hideBackdrop: true,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                  },
                })}
                error={Boolean(errors.communicationPreferences?.message)}
                errorMessage={errors.communicationPreferences?.message}
                sx={{
                  width: "100%",
                  ...(isPreview && {
                    opacity: 1,
                    pointerEvents: "none", // only affects text field not drop down
                    backgroundColor: theme.palette.surface.neutral.white,
                    borderColor: theme.palette.border.neutral.quiet,
                    borderRadius: theme.radius(1),
                  }),
                }}
              />
            )}
          </Stack>
        ),
        submitButton: (
          <DashboardButton
            variant="filled"
            fullWidth
            type="submit"
            size="large"
            isLoading={submitState === "submitting"}
            disabled={isPreview}
            style={
              isPreview
                ? {
                    backgroundColor: buttonColor,
                    color: buttonTextColor,
                    cursor: "initial",
                    opacity: 1,
                    pointerEvents: "none",
                  }
                : {}
            }
          >
            <Box width="100%" color={buttonTextColor}>
              {submitState === "submitted" ? t("common", "Submitted.exclamation") : t("common", "Submit")}
            </Box>
          </DashboardButton>
        ),
      }}
    />
  );
};
